import { useEffect } from 'react';

interface IConditionalPollingParams {
  /** Apollo `startPolling` from a query result. */
  startPolling: (interval: number) => void;
  /** Apollo `stopPolling` from a query result. */
  stopPolling: () => void;
  /** Poll interval in ms. */
  pollInterval: number;
  /** Poll only while this is true (e.g. there are rows in an active status). */
  predicate?: boolean;
  /** Pause polling while the tab is hidden. Default true. */
  visibilityAware?: boolean;
}

/**
 * Drives Apollo polling on/off from a predicate and (optionally) tab
 * visibility. Used by Zoom screens so polling stops when there is nothing
 * in-flight or the operator switched away.
 */
export const useConditionalPolling = ({
  startPolling,
  stopPolling,
  pollInterval,
  predicate = true,
  visibilityAware = true,
}: IConditionalPollingParams): void => {
  useEffect(() => {
    const sync = () => {
      const tabVisible =
        !visibilityAware || document.visibilityState === 'visible';

      if (predicate && tabVisible) {
        startPolling(pollInterval);
      } else {
        stopPolling();
      }
    };

    sync();

    if (visibilityAware) {
      document.addEventListener('visibilitychange', sync);
    }

    return () => {
      if (visibilityAware) {
        document.removeEventListener('visibilitychange', sync);
      }
      stopPolling();
    };
  }, [startPolling, stopPolling, pollInterval, predicate, visibilityAware]);
};
